import { useState, type FormEvent } from 'react';
import {
  peopleApi,
  rolesApi,
  partnersApi,
  clientsApi,
  type Person,
  type PersonWrite,
  type Partner,
  type DeviceSession,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { errorText } from '@/components/FlashMessage';
import { formatAgo, formatDateTime, secondsSince } from '@/lib/format';

function displayName(p: Person) {
  return p.fullName ?? p.name ?? '—';
}

const CLIENT_PORTAL_ROLE_NAMES = new Set(['Client Main', 'Client Sub', 'Client Viewer']);

const EMPTY_FORM: PersonWrite = {
  fullName: '',
  mobile: '',
  engagementType: 'employee',
  isCrew: true,
  isSales: false,
  salesEngagement: 'full_time',
  licenceNumber: '',
  licenceExpiry: '',
  supplyingPartnerId: null,
};

export function AdminPeople() {
  const [showArchived, setShowArchived] = useState(false);
  const { data: people, loading, error: loadError, reload } = useAsync(
    () => peopleApi.list({ includeArchived: showArchived }),
    [showArchived],
  );
  const { data: systemRoles } = useAsync(() => rolesApi.assignable(), []);
  const { data: partners } = useAsync(() => partnersApi.list(), []);
  const { data: clients } = useAsync(() => clientsApi.list(), []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [form, setForm] = useState<PersonWrite>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [loginClientId, setLoginClientId] = useState('');
  const confirm = useConfirm();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [loginTarget, setLoginTarget] = useState<Person | null>(null);
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    status: 'active' as 'active' | 'disabled' | 'pending_invite',
  });
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [loginSaving, setLoginSaving] = useState(false);
  const [loginError, setLoginError] = useState('');

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Person) => {
    setEditing(row);
    setForm({
      fullName: row.fullName ?? row.name ?? '',
      mobile: row.mobile ?? row.phone ?? '',
      engagementType: row.engagementType ?? 'employee',
      isCrew: row.isCrew ?? false,
      isSales: row.isSales ?? false,
      salesEngagement: row.salesEngagement ?? 'full_time',
      licenceNumber: row.licenceNumber ?? '',
      licenceExpiry: row.licenceExpiry ? row.licenceExpiry.slice(0, 10) : '',
      supplyingPartnerId: row.supplyingPartnerId ?? null,
    });
    setError('');
    setShowForm(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload: PersonWrite = {
        fullName: form.fullName.trim(),
        mobile: form.mobile || undefined,
        engagementType: form.engagementType,
        isCrew: form.isCrew ?? false,
        isSales: form.isSales ?? false,
        licenceNumber: form.licenceNumber || undefined,
        licenceExpiry: form.licenceExpiry || undefined,
        supplyingPartnerId:
          form.engagementType === 'contractor' ? form.supplyingPartnerId || null : null,
      };
      if (editing) {
        await peopleApi.update(editing.id, payload);
        toast(`${payload.fullName} updated`);
      } else {
        await peopleApi.create(payload);
        toast(`${payload.fullName} added${payload.isCrew ? ' as crew' : ''}`);
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      setError(errorText(err, 'Could not save this person.'));
    } finally {
      setSaving(false);
    }
  };

  const openLogin = (row: Person) => {
    setLoginTarget(row);
    setLoginForm({
      email: row.loginEmail ?? row.email ?? '',
      password: '',
      status: (row.loginStatus as 'active' | 'disabled' | 'pending_invite') || 'active',
    });
    setSelectedRoleIds((row.systemRoles ?? []).map((r) => r.id));
    // Existing client-portal logins already belong to a client — preselect it so
    // saving doesn't demand it again.
    setLoginClientId(row.clientId ?? '');
    setLoginError('');
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const saveLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!loginTarget) return;
    const hasLogin = !!loginTarget.hasLogin;
    if (!hasLogin && !loginForm.password.trim()) {
      setLoginError('Temporary password is required when creating a login');
      return;
    }
    const selectedNames = (systemRoles ?? [])
      .filter((r) => selectedRoleIds.includes(r.id))
      .map((r) => r.name);
    const needsClient = selectedNames.some((n) => CLIENT_PORTAL_ROLE_NAMES.has(n));
    if (needsClient && !loginClientId) {
      setLoginError('Choose which client company this person belongs to (needed for client portal roles).');
      return;
    }
    setLoginSaving(true);
    setLoginError('');
    try {
      if (hasLogin) {
        await peopleApi.updateAccount(loginTarget.id, {
          email: loginForm.email.trim(),
          password: loginForm.password.trim() || undefined,
          roleIds: selectedRoleIds,
          status: loginForm.status,
          clientId: needsClient ? loginClientId : undefined,
        });
      } else {
        await peopleApi.linkAccount(loginTarget.id, {
          email: loginForm.email.trim(),
          password: loginForm.password.trim(),
          roleIds: selectedRoleIds,
          clientId: needsClient ? loginClientId : undefined,
        });
      }
      toast(hasLogin ? `Login for ${displayName(loginTarget)} saved` : `Login created for ${displayName(loginTarget)}`);
      setLoginTarget(null);
      reload();
    } catch (err) {
      setLoginError(errorText(err, 'Could not save the login.'));
    } finally {
      setLoginSaving(false);
    }
  };

  const archivePerson = async (row: Person) => {
    const confirmed = await confirm({
      title: `Archive ${displayName(row)}?`,
      message: `${displayName(row)} will be hidden from crew pickers and lists${row.hasLogin ? ' and won’t be able to sign in' : ''}. Their trip history is kept, and you can restore them any time from “Show archived”.`,
      confirmLabel: 'Archive',
    });
    if (!confirmed) return;
    setActionError('');
    try {
      await peopleApi.archive(row.id);
      toast(`${displayName(row)} archived`);
      reload();
    } catch (err) {
      setActionError(errorText(err, `Could not archive ${displayName(row)}.`));
    }
  };

  const restorePerson = async (row: Person) => {
    setActionError('');
    try {
      await peopleApi.restore(row.id);
      toast(`${displayName(row)} restored`);
      reload();
    } catch (err) {
      setActionError(errorText(err, `Could not restore ${displayName(row)}.`));
    }
  };

  const deletePerson = async (row: Person) => {
    const confirmed = await confirm({
      title: `Permanently delete ${displayName(row)}?`,
      message: `This removes ${displayName(row)}${row.hasLogin ? ' and their login' : ''} for good and can’t be undone. If they have worked on any trips, archive them instead so the history stays intact.`,
      confirmLabel: 'Delete permanently',
      danger: true,
    });
    if (!confirmed) return;
    setActionError('');
    try {
      await peopleApi.remove(row.id);
      toast(`${displayName(row)} deleted`);
      reload();
    } catch (err) {
      setActionError(errorText(err, `Could not delete ${displayName(row)}.`));
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      value: (row: Person) => displayName(row),
      render: (row: Person) => displayName(row),
    },
    {
      key: 'mobile',
      header: 'Phone',
      value: (row: Person) => row.mobile ?? row.phone ?? '',
      render: (row: Person) => row.mobile ?? row.phone ?? '—',
    },
    {
      key: 'type',
      header: 'Type',
      value: (row: Person) => (row.engagementType === 'contractor' ? 'Contractor' : 'Employee'),
      render: (row: Person) => (row.engagementType === 'contractor' ? 'Contractor' : 'Employee'),
    },
    {
      key: 'partner',
      header: 'Partner',
      value: (row: Person) => row.partnerName ?? '',
      render: (row: Person) =>
        row.engagementType === 'contractor'
          ? (row.partnerName ?? <span className="text-muted">Unassigned</span>)
          : '—',
    },
    {
      key: 'login',
      header: 'Login',
      value: (row: Person) => (row.hasLogin ? row.loginEmail ?? '' : ''),
      render: (row: Person) =>
        row.hasLogin ? (
          <span className="login-cell">
            <span>{row.loginEmail}</span>
            {row.loginStatus && row.loginStatus !== 'active' && (
              <StatusBadge status={row.loginStatus} size="sm" />
            )}
          </span>
        ) : (
          <span className="text-muted">No login</span>
        ),
    },
    {
      key: 'systemRoles',
      header: 'System roles',
      value: (row: Person) => row.systemRoles?.map((r) => r.name).join(', ') ?? '',
      render: (row: Person) =>
        row.systemRoles?.length
          ? row.systemRoles.map((r) => r.name).join(', ')
          : row.hasLogin
            ? <span className="text-muted">None assigned</span>
            : '—',
    },
    {
      key: 'crew',
      header: 'Crew',
      value: (row: Person) => row.isCrew ?? false,
      render: (row: Person) => (row.isCrew ? 'Yes' : 'No'),
    },
    {
      key: 'status',
      header: 'Status',
      value: (row: Person) => row.status,
      render: (row: Person) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: Person) => (
        <div className="row-actions">
          <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => openLogin(row)}>
            {row.hasLogin ? 'Manage login' : 'Create login'}
          </Button>
          {row.status === 'archived' ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => void restorePerson(row)}>Restore</Button>
          ) : (
            <Button type="button" size="sm" variant="ghost" onClick={() => void archivePerson(row)}>Archive</Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => void deletePerson(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  const managingExisting = !!loginTarget?.hasLogin;

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>People</h1>
            <p className="text-muted">Crew, drivers, and staff — logins and system roles</p>
          </div>
          <div className="row">
            <label className="show-archived">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <Button onClick={openAdd}>Add person</Button>
          </div>
        </div>

        {actionError && <p className="text-urgent">{actionError}</p>}

        <DataTable
          columns={columns}
          data={people ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter people…"
          emptyMessage="No people added. Register crew and drivers to assign trips."
        />

        <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit person' : 'Add person'}>
          <form onSubmit={save}>
            <Input
              label="Full name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              required
            />
            <Input
              label="Phone"
              value={form.mobile ?? ''}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              placeholder="+63 917 000 0000"
            />
            <div className="grid-2">
              <Select
                label="Engagement"
                value={form.engagementType ?? 'employee'}
                onChange={(e) => {
                  const engagementType = e.target.value as PersonWrite['engagementType'];
                  setForm({
                    ...form,
                    engagementType,
                    supplyingPartnerId:
                      engagementType === 'contractor' ? form.supplyingPartnerId : null,
                  });
                }}
              >
                <option value="employee">Employee</option>
                <option value="contractor">Contractor</option>
              </Select>
              <Select
                label="Crew member"
                value={form.isCrew ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, isCrew: e.target.value === 'yes' })}
                hint="Crew can be set as standing driver/helpers on vehicles"
              >
                <option value="yes">Yes — driver / helper</option>
                <option value="no">No — office / other</option>
              </Select>
            </div>
            {form.engagementType === 'contractor' && (
              <Select
                label="Supplying partner"
                value={form.supplyingPartnerId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, supplyingPartnerId: e.target.value || null })
                }
                hint="Assign this contractor to a subcontractor partner"
              >
                <option value="">Unassigned</option>
                {(partners ?? []).map((p: Partner) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            )}
            <div className="grid-2">
              <Input
                label="Licence number"
                value={form.licenceNumber ?? ''}
                onChange={(e) => setForm({ ...form, licenceNumber: e.target.value })}
              />
              <Input
                label="Licence expiry"
                type="date"
                value={form.licenceExpiry ?? ''}
                onChange={(e) => setForm({ ...form, licenceExpiry: e.target.value })}
              />
            </div>
            <Select
              label="Sales role"
              value={form.isSales ? 'yes' : 'no'}
              onChange={(e) => setForm({ ...form, isSales: e.target.value === 'yes' })}
              hint="Eligible to be assigned as a client's sales owner"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Select>
            {form.isSales && (
              <Select
                label="Sales engagement"
                value={form.salesEngagement ?? 'full_time'}
                onChange={(e) =>
                  setForm({
                    ...form,
                    salesEngagement: e.target.value as 'full_time' | 'part_time' | 'commission_only',
                  })
                }
                hint="Full-time sees per-trip cost/GP; part-time sees aggregate GP only"
              >
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="commission_only">Commission only</option>
              </Select>
            )}
            {error && <p className="text-urgent">{error}</p>}
            <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Create person'}</Button>
          </form>
        </Modal>

        <Modal
          open={!!loginTarget}
          onClose={() => setLoginTarget(null)}
          title={
            loginTarget
              ? `${managingExisting ? 'Manage login' : 'Create login'} · ${displayName(loginTarget)}`
              : 'Login'
          }
        >
          <form onSubmit={saveLogin}>
            <Input
              label="Email"
              type="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              required
            />
            <Input
              label={managingExisting ? 'New temporary password' : 'Temporary password'}
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              required={!managingExisting}
              minLength={managingExisting && !loginForm.password ? undefined : 10}
              hint={
                managingExisting
                  ? 'Leave blank to keep the current password. New passwords force reset on next login.'
                  : 'Min 10 characters — user resets on first login'
              }
            />
            {managingExisting && (
              <Select
                label="Account status"
                value={loginForm.status}
                onChange={(e) =>
                  setLoginForm({
                    ...loginForm,
                    status: e.target.value as 'active' | 'disabled' | 'pending_invite',
                  })
                }
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="pending_invite">Pending invite</option>
              </Select>
            )}

            <fieldset className="role-fieldset">
              <legend>System roles</legend>
              <p className="text-muted role-hint">
                Assign Dispatcher, Admin, Crew, and other access roles. Permissions come from these roles.
              </p>
              <div className="role-grid">
                {(systemRoles ?? []).map((role) => (
                  <label key={role.id} className="role-check">
                    <input
                      type="checkbox"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
                {!systemRoles?.length && (
                  <p className="text-muted">No roles available yet. Check Settings → Roles.</p>
                )}
              </div>
            </fieldset>

            {(systemRoles ?? []).some(
              (r) => selectedRoleIds.includes(r.id) && CLIENT_PORTAL_ROLE_NAMES.has(r.name),
            ) && (
              <Select
                label="Client organisation"
                value={loginClientId}
                onChange={(e) => setLoginClientId(e.target.value)}
                required
                hint="The client company whose trips this person can see in the portal"
              >
                <option value="">Select client…</option>
                {(clients ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.tradeName || c.legalName}
                  </option>
                ))}
              </Select>
            )}

            {loginError && <p className="text-urgent">{loginError}</p>}
            <Button type="submit" loading={loginSaving}>
              {managingExisting ? 'Save login' : 'Create login'}
            </Button>
          </form>
          {managingExisting && loginTarget && hasPermission('users.manage') && (
            <SignedInPhones personId={loginTarget.id} personName={displayName(loginTarget)} />
          )}
        </Modal>

        <style>{`
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; flex-wrap: wrap; }
          .show-archived {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            font-size: 0.875rem;
            color: var(--color-text-muted);
            margin-right: var(--space-sm);
          }
          .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
          }
          .login-cell {
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
            font-size: 0.875rem;
          }
          .role-fieldset {
            border: 1px solid var(--color-border);
            border-radius: var(--radius-sm, 4px);
            padding: var(--space-md);
            margin: var(--space-md) 0;
          }
          .role-fieldset legend {
            padding: 0 var(--space-xs);
            font-weight: 600;
            font-size: 0.875rem;
          }
          .role-hint {
            font-size: 0.8125rem;
            margin: 0 0 var(--space-sm);
          }
          .role-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-xs) var(--space-md);
          }
          .role-check {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
            cursor: pointer;
          }
          @media (max-width: 640px) {
            .grid-2, .role-grid { grid-template-columns: 1fr; }
          }
        `}</style>
      </div>
    </Layout>
  );
}

/** Phones this person is signed in on with the crew app, with "Sign out this phone". */
function SignedInPhones({ personId, personName }: { personId: string; personName: string }) {
  const { data, loading, error, reload } = useAsync(() => peopleApi.devices(personId), [personId]);
  const confirm = useConfirm();
  const toast = useToast();
  const [actionError, setActionError] = useState('');

  const signOut = async (d: DeviceSession) => {
    const phone = d.deviceName || 'this phone';
    const ok = await confirm({
      title: `Sign out ${phone}?`,
      message: `${personName} will be signed out of the crew app on ${phone} and will stop getting notifications there. They can sign in again with their email and password.`,
      confirmLabel: 'Sign out phone',
      danger: true,
    });
    if (!ok) return;
    setActionError('');
    try {
      await peopleApi.signOutDevice(personId, d.id);
      toast(`${phone} signed out`);
      reload();
    } catch (err) {
      setActionError(errorText(err, 'Could not sign out that phone.'));
    }
  };

  return (
    <section className="phones-block">
      <h3>Signed-in phones</h3>
      <p className="text-muted phones-hint">
        Phones where {personName} is signed in to the crew app. Sign out a phone that is lost or no longer used.
      </p>
      {actionError && <p className="text-urgent">{actionError}</p>}
      <DataTable
        data={data ?? []}
        keyField="id"
        loading={loading}
        loadError={error}
        onRetry={reload}
        dense
        emptyMessage="Not signed in to the crew app on any phone."
        columns={[
          {
            key: 'deviceName',
            header: 'Phone',
            render: (d: DeviceSession) => (
              <span>
                {d.deviceName || 'Android phone'}
                {!d.pushEnabled && <span className="text-muted"> · notifications off</span>}
              </span>
            ),
          },
          { key: 'appVersion', header: 'App version', render: (d: DeviceSession) => d.appVersion ?? '—' },
          {
            key: 'lastSeenAt',
            header: 'Last seen',
            render: (d: DeviceSession) => <span title={formatDateTime(d.lastSeenAt)}>{formatAgo(secondsSince(d.lastSeenAt))}</span>,
          },
          { key: 'signedInAt', header: 'Signed in', render: (d: DeviceSession) => formatDateTime(d.signedInAt) },
          {
            key: 'actions',
            header: '',
            sortable: false,
            align: 'right',
            render: (d: DeviceSession) => (
              <Button type="button" size="sm" variant="ghost" onClick={() => void signOut(d)}>
                Sign out this phone
              </Button>
            ),
          },
        ]}
      />
      <style>{`
        .phones-block {
          margin-top: var(--space-xl);
          padding-top: var(--space-lg);
          border-top: 1px solid var(--color-border);
        }
        .phones-block h3 { font-size: 1rem; margin-bottom: var(--space-xs); }
        .phones-hint { font-size: 0.8125rem; margin: 0 0 var(--space-sm); }
      `}</style>
    </section>
  );
}
